import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { routes } from 'app/routing/routesConfig';
import { ContentContainer } from 'app/components/Core';
import { AudioPlayerAudioInstance } from 'app/store/AudioPlayer/slice.types';
import { ContentContainerType } from 'types/enums';
import { setQueueIndexAction } from 'app/store/AudioPlayer/slice';
import { PlaylistDescriptionDialog } from 'app/components/Playlist/PlaylistDescriptionDialog';
import { PlaylistAudioNoteDialog } from 'app/components/Playlist/PlaylistAudioNoteDialog';
import { useClickedOutsideElement } from 'app/utils/hooks';
import {
  getAudioPlayer,
  getPlayerHeight,
} from 'app/store/AudioPlayer/selectors';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useNavigate } from 'react-router-dom';
import { PlaylistRoomHeader } from 'app/components/Playlist/PlaylistRoomHeader';
import { AudioPlayerPlaylistTable } from './AudioPlayerPlaylistTable/AudioPlayerPlaylistTable';

export const AudioPlayerPlaylistView = () => {
  const { isOpen, playlistDetails, queue, images } = useAppSelector(
    getAudioPlayer
  );

  const navigate = useNavigate();
  const [
    viewedNoteAudio,
    setViewedNoteAudio,
  ] = useState<AudioPlayerAudioInstance | null>(null);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const playerHeight = useAppSelector(getPlayerHeight);

  const { ref } = useClickedOutsideElement<HTMLDivElement>((event) => {
    if (
      viewedNoteAudio ||
      descriptionDialogOpen ||
      document.body.clientHeight - event.y < playerHeight
    ) {
      return;
    }
    navigate(-1);
  });

  const audios = useMemo(
    () =>
      queue.map(({ audioGuid, audioTitle, duration }) => ({
        guid: audioGuid,
        title: audioTitle!,
        duration: duration!,
      })),
    [queue]
  );

  const dispatch = useAppDispatch();
  const setQueueIndex = useCallback(
    (index: number) => dispatch(setQueueIndexAction(index)),
    [dispatch]
  );

  useEffect(() => {
    if (!isOpen) navigate(-1);
  }, [isOpen, navigate]);

  return (
    <>
      <Box ref={ref}>
        {playlistDetails && (
          <PlaylistRoomHeader
            guid={playlistDetails.guid}
            title={playlistDetails.title}
            loadPlaylistAudio={async (playIndex) => {
              setQueueIndex(playIndex);
            }}
            audios={audios}
            images={images}
            readonly
            hideGradiant
            playlistIconLink={routes.playlist.path.replace(
              ':guid',
              playlistDetails.guid
            )}
          />
        )}
        <ContentContainer type={ContentContainerType.LIST} isViewContent pt={0}>
          {playlistDetails?.description && (
            <Text
              role="button"
              onClick={() => setDescriptionDialogOpen(true)}
              data-testid="playlist-description-text"
              maxW="600px"
              noOfLines={2}
              mb={6}
              wordBreak="break-all"
              _hover={{
                textDecoration: 'underline',
              }}
            >
              {playlistDetails?.description}
            </Text>
          )}
          <AudioPlayerPlaylistTable
            queue={queue}
            setViewedNoteAudio={setViewedNoteAudio}
          />
        </ContentContainer>
      </Box>
      <PlaylistAudioNoteDialog
        open={Boolean(viewedNoteAudio)}
        onClose={() => setViewedNoteAudio(null)}
        message={viewedNoteAudio?.note}
        title={viewedNoteAudio?.audioTitle}
      />
      <PlaylistDescriptionDialog
        open={descriptionDialogOpen}
        onClose={() => setDescriptionDialogOpen(false)}
        description={playlistDetails?.description}
        title={playlistDetails?.title || ''}
      />
    </>
  );
};
